import { useState } from 'react';
import { X } from 'lucide-react';

interface DeleteModalProps {
  chatTitle: string;
  /** Return `false` to keep the modal open (e.g. delete failed). */
  onDelete: () => boolean | void | Promise<boolean | void>;
  onClose: () => void;
}

export function DeleteModal({ chatTitle, onDelete, onClose }: DeleteModalProps) {
  const [pending, setPending] = useState(false);

  const handleDelete = async () => {
    if (pending) return;
    setPending(true);
    try {
      const result = await onDelete();
      if (result !== false) {
        onClose();
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Delete chat?</h2>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-slate-600 mb-6">
          Are you sure you want to delete <span className="font-medium text-slate-900">"{chatTitle}"</span>? This action cannot be undone.
        </p>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={pending}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {pending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
